// src/hooks/useSubscription.js
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook pour gérer les profils et abonnements utilisateurs
 * 
 * Rôles:
 * - gratuit: 5 matchs max, toutes fonctionnalités sauf Expert
 * - normal: Matchs illimités, toutes fonctionnalités sauf Expert
 * - expert: Matchs illimités, toutes fonctionnalités
 * - admin: Accès total + gestion utilisateurs
 * 
 * @returns {Object} Informations sur le profil et les droits d'accès
 */
export const useSubscription = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setLoading(false);
          return;
        }
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Erreur chargement profil:', error);
          setLoading(false);
          return;
        }
        
        setProfile(data);
      } catch (error) {
        console.error('Erreur useSubscription:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
    
    // Écouter les changements d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchProfile();
    });
    
    return () => subscription.unsubscribe();
  }, []);
  
  // Vérifier si l'essai est encore valide
  const isTrialActive = profile?.subscription_status === 'trial' && 
                        profile?.trial_ends_at &&
                        new Date(profile.trial_ends_at) > new Date();
  
  // Vérifier si l'abonnement est actif
  const isSubscriptionActive = profile?.subscription_status === 'active' && 
                                profile?.subscription_ends_at &&
                                new Date(profile.subscription_ends_at) > new Date();
  
  // L'utilisateur a-t-il accès aux fonctionnalités payantes ?
  const hasAccess = isTrialActive || isSubscriptionActive || profile?.role === 'admin';
  
  // Nombre de matchs restants (gratuit uniquement)
  const matchesRemaining = profile?.role === 'gratuit' 
    ? Math.max(0, (profile?.matches_limit || 5) - (profile?.matches_count || 0))
    : Infinity;
  
  // Jours d'essai restants
  const trialDaysRemaining = profile?.trial_ends_at 
    ? Math.max(0, Math.ceil((new Date(profile.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;
  
  return {
    // Données brutes
    profile,
    loading,
    
    // Rôles
    role: profile?.role || 'gratuit',
    isGratuit: profile?.role === 'gratuit',
    isNormal: profile?.role === 'normal',
    isExpert: profile?.role === 'expert',
    isAdmin: profile?.role === 'admin',
    
    // Accès aux fonctionnalités
    hasAccess,  // A accès aux fonctionnalités payantes (normal/expert)
    hasExpertAccess: (profile?.role === 'expert' || profile?.role === 'admin') && hasAccess,
    hasAdminAccess: profile?.role === 'admin',
    
    // Statut abonnement
    subscriptionStatus: profile?.subscription_status || 'inactive',
    isTrialActive,
    isSubscriptionActive,
    
    // Essai gratuit
    trialEndsAt: profile?.trial_ends_at,
    trialDaysRemaining,
    
    // Limites version gratuite
    matchesCount: profile?.matches_count || 0,
    matchesLimit: profile?.matches_limit || 5,
    matchesRemaining,
    canCreateMatch: profile?.role !== 'gratuit' || matchesRemaining > 0,
    
    // Fonction helper pour rafraîchir le profil
    refreshProfile: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        setProfile(data);
      }
    }
  };
};